import { Sparkles } from "lucide-react";
import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Progress } from "../../../../components/ui/progress";
import { useAICredits } from "../../../../services/ai/aiModelHooks";
import type { AICreditsSurfaceUsage } from "../../../../services/ai/aiModels.types";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";

const SURFACE_LABELS: Record<string, string> = {
  agent: "Agent",
  signals: "Signals",
  health: "Health",
  background: "Background",
};

const surfaceLabel = (surface: string) =>
  SURFACE_LABELS[surface]
  ?? surface
    .split(/[-_\s]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCredits = (value: number) =>
  (Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AICreditsPage = () => {
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const { data, isLoading, isError } = useAICredits({ workspaceId });

  const usagePercent = useMemo(() => {
    if (!data || data.allotment <= 0) return 0;
    return Math.min(100, Math.max(0, (data.used / data.allotment) * 100));
  }, [data]);

  const surfaces: AICreditsSurfaceUsage[] = data?.by_surface ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-lg font-medium text-foreground">AI Credits</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Track your workspace&apos;s AI credit balance and usage.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 rounded-lg border border-border-strong bg-card px-4 py-8 text-[13px] text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
          Loading credits…
        </div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-border-strong bg-card px-4 py-8 text-center text-[13px] text-muted-foreground">
          Unable to load AI credits. Please try again later.
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="rounded-lg border border-border-strong bg-card p-6">
            {data.allotment <= 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <Sparkles className="mb-2 h-5 w-5 text-muted-foreground" />
                <p className="text-[13px] font-medium text-foreground">No allotment</p>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  This workspace doesn&apos;t have an AI credit allotment yet.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Credits used
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      {formatCredits(data.used)}
                      <span className="text-base font-normal text-muted-foreground">
                        {" / "}
                        {formatCredits(data.allotment)}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Remaining
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-foreground">
                      {formatCredits(data.balance)}
                    </p>
                  </div>
                </div>
                <Progress value={usagePercent} className="mt-4 h-2" />
                <p className="mt-2 text-[12px] text-muted-foreground">
                  {formatCredits(data.used)} of {formatCredits(data.allotment)}{" "}
                  credits used · {formatCredits(data.balance)} remaining
                </p>
              </>
            )}
          </div>

          {/* Total usage + period */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border-strong bg-card p-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Total credits used
              </p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {formatCredits(data.total_credits_used)}
              </p>
            </div>
            {data.period_start && data.period_end ? (
              <div className="rounded-lg border border-border-strong bg-card p-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Current period
                </p>
                <p className="mt-1 text-[13px] text-foreground">
                  {formatDate(data.period_start)} – {formatDate(data.period_end)}
                </p>
              </div>
            ) : null}
          </div>

          {/* Breakdown */}
          <div>
            <h2 className="mb-1 text-[13px] font-medium text-foreground">
              Usage by surface
            </h2>
            <p className="mb-4 text-[11px] text-muted-foreground">
              How credits are consumed across the assistant, signals, health
              scoring, and background processing
            </p>
            {surfaces.length === 0 ? (
              <div className="rounded-lg border border-border-strong bg-card px-4 py-8 text-center text-[13px] text-muted-foreground">
                No usage recorded yet.
              </div>
            ) : (
              <div className="rounded-lg border border-border-strong bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-10 text-[12px]">Surface</TableHead>
                      <TableHead className="h-10 text-right text-[12px]">
                        Credits
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {surfaces.map((row) => (
                      <TableRow key={row.surface} className="hover:bg-transparent">
                        <TableCell className="py-2.5 text-[13px] text-foreground">
                          {surfaceLabel(row.surface)}
                        </TableCell>
                        <TableCell className="py-2.5 text-right text-[13px] text-foreground">
                          {formatCredits(row.credits)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AICreditsPage;
